import * as React from "react";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Loader } from "lucide-react";
import { FaRegFilePdf, FaRegCopy } from "react-icons/fa";
import { BsArrowReturnLeft } from "react-icons/bs";
import { useMessageActions } from "../hooks/useMessageActions";
import { ChatMessage } from "../../types/discuss.types";
import { cn } from "@/lib/utils";

interface DiscussBulkActionsProps {
  messages: ChatMessage[];
  documentId?: string;
  documentTitle?: string;
  className?: string;
}

const ACTION_BUTTON_CLASS =
  "h-7 w-7 rounded-full bg-neutral-700 text-neutral-200 hover:bg-neutral-600";

const BulkActionButton: React.FC<{
  icon: React.ReactNode;
  tooltip: string;
  onClick: () => void;
  isLoading?: boolean;
  disabled?: boolean;
}> = ({ icon, tooltip, onClick, isLoading = false, disabled = false }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className={ACTION_BUTTON_CLASS}
        onClick={onClick}
        disabled={disabled || isLoading}
      >
        {isLoading ? <Loader className="size-[14px] animate-spin" /> : icon}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="left">{tooltip}</TooltipContent>
  </Tooltip>
);

function getConversationText(messages: ChatMessage[]) {
  return messages
    .map((m) => `**User:** ${m.question} \n\n **Assistant:** ${m.response} `)
    .join("\n\n---\n\n");
}

async function withLoading(
  setter: React.Dispatch<React.SetStateAction<boolean>>,
  fn: (text: string) => Promise<void>,
  text: string
) {
  if (text.length === 0) return;
  setter(true);
  try {
    await fn(text);
  } finally {
    setter(false);
  }
}

const DiscussBulkActions: React.FC<DiscussBulkActionsProps> = ({
  messages,
  documentId,
  documentTitle,
  className,
}) => {
  const [isCreatingDoc, setIsCreatingDoc] = React.useState(false);
  const [isCopying, setIsCopying] = React.useState(false);
  const [isInserting, setIsInserting] = React.useState(false);

  const { handleCopyMessage, handleInsertMessageToDocument, handleCreateDocumentFromMessage } =
    useMessageActions({ documentId, documentTitle });

  const handleCreate = () =>
    withLoading(setIsCreatingDoc, handleCreateDocumentFromMessage, getConversationText(messages));
  const handleCopy = () =>
    withLoading(setIsCopying, handleCopyMessage, getConversationText(messages));
  const handleInsert = () =>
    withLoading(setIsInserting, handleInsertMessageToDocument, getConversationText(messages));

  if (messages.length === 0) return null;

  const isAnyLoading = isCreatingDoc || isCopying || isInserting;

  return (
    <TooltipProvider>
      <div className={cn("flex flex-col gap-2", className)}>
        <BulkActionButton
          icon={<FaRegFilePdf className="size-[14px]" />}
          tooltip="Create document from conversation"
          onClick={handleCreate}
          isLoading={isCreatingDoc}
          disabled={isAnyLoading}
        />
        <BulkActionButton
          icon={<FaRegCopy className="size-[14px]" />}
          tooltip="Copy conversation"
          onClick={handleCopy}
          isLoading={isCopying}
          disabled={isAnyLoading}
        />
        <BulkActionButton
          icon={<BsArrowReturnLeft className="size-[14px]" />}
          tooltip="Insert conversation"
          onClick={handleInsert}
          isLoading={isInserting}
          disabled={isAnyLoading}
        />
      </div>
    </TooltipProvider>
  );
};

export default DiscussBulkActions;
